/**
 * Shared microphone acquisition flow — the impure companion to `mic-availability.ts`.
 *
 * `mic-availability` keeps the pure classification (error name + permission state →
 * MicStatus); this module gathers the browser signals (Permissions API, iframe
 * Permissions-Policy) and runs the acquire-and-classify sequence. Every host reaches
 * it through `DealerSDK.startPTT` (the iframe app rides the SDK path too), so the
 * mic-blocked UX classifies identically regardless of embedding.
 */

import {
  classifyMicError,
  resolveMicErrorName,
  type MicPermissionState,
  type MicStatus,
} from './mic-availability.js';

/**
 * Read the mic permission state. Firefox throws for {name:'microphone'} (not in its
 * PermissionName set) and Safari support is partial — both collapse to 'unsupported',
 * which the classifier treats as recoverable (re-prompt on the next hold). Documented so
 * a future change does not "fix" this into a hang.
 */
export async function queryMicPermission(): Promise<MicPermissionState> {
  try {
    const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
    const state = status?.state;
    return state === 'granted' || state === 'denied' || state === 'prompt' ? state : 'unsupported';
  } catch {
    return 'unsupported';
  }
}

/**
 * Whether the iframe's Permissions-Policy disallows the microphone (host forgot
 * allow="microphone"). Only Chromium exposes document.featurePolicy; elsewhere we can't
 * tell and assume allowed.
 */
export function isMicPolicyBlocked(): boolean {
  try {
    const fp = (
      document as unknown as {
        featurePolicy?: { allowsFeature?: (feature: string) => boolean };
      }
    ).featurePolicy;
    if (fp?.allowsFeature) {
      return !fp.allowsFeature('microphone');
    }
  } catch {
    /* no featurePolicy API → can't tell, assume allowed */
  }
  return false;
}

/** The mic track surface needed for dead-on-arrival detection. */
export interface MicrophoneTrack {
  readonly mediaStreamTrack?: { readonly readyState: string; readonly muted: boolean };
  restartTrack?(): Promise<unknown>;
}

/** The slice of LiveKit's LocalParticipant the acquisition flow needs. */
export interface MicrophoneControl {
  readonly isMicrophoneEnabled: boolean;
  setMicrophoneEnabled(enabled: boolean): Promise<unknown>;
  getTrackPublication?(source: string): { track?: MicrophoneTrack } | undefined;
}

/**
 * Enable the microphone (first call rides the user gesture so the browser permission
 * prompt appears) and classify the outcome. Already-enabled is a no-op 'ok'. On failure
 * the mic stays disabled and the returned status tells the widget how to guide the
 * player (`prompt` — silent retry; `blocked` — settings guidance; `unavailable` — no
 * mic to be had).
 */
export async function acquireMicrophone(participant: MicrophoneControl): Promise<MicStatus> {
  if (participant.isMicrophoneEnabled) {
    return 'ok';
  }
  try {
    await participant.setMicrophoneEnabled(true);
    await restartDeadCapture(participant);
    return 'ok';
  } catch (err) {
    const errorName = resolveMicErrorName(err);
    const permissionState = await queryMicPermission();
    return classifyMicError(errorName, permissionState, isMicPolicyBlocked());
  }
}

/**
 * iOS can hand over a capture that is already `ended` right after the permission
 * grant — it records nothing, and only a fresh MediaStreamTrack recovers it
 * (86ak3kdq4). `ended` is terminal and unambiguous, so restarting on it is safe.
 *
 * `muted` is deliberately NOT treated as dead. A newborn iOS track reads muted
 * until frames flow, so testing it fired on every healthy first grant and cost a
 * SECOND getUserMedia at the exact moment the audio session is being
 * established — which is when the dealer's voice starts sounding processed and
 * stops reaching screen recordings (#476, regression window 2026-08-27→29).
 * The only capture ever OBSERVED dead was `ended`, and even that turned out to
 * be a simulator artifact; the muted arm was defensive and never evidence-backed.
 */
async function restartDeadCapture(participant: MicrophoneControl): Promise<void> {
  const track = participant.getTrackPublication?.('microphone')?.track;
  const capture = track?.mediaStreamTrack;
  if (!track?.restartTrack || !capture) {
    return;
  }
  if (capture.readyState === 'ended') {
    try {
      await track.restartTrack();
    } catch (err) {
      // The dead track is still published-and-enabled; left like this the next
      // hold would short-circuit on isMicrophoneEnabled onto silence. Disable
      // so the failure contract holds ("on failure the mic stays disabled")
      // and the next hold re-acquires through the normal path.
      try {
        await participant.setMicrophoneEnabled(false);
      } catch {
        /* best effort — the classifier below still reports the real failure */
      }
      throw err;
    }
    // restartTrack resolves without inspecting its replacement (LocalTrack.
    // restart returns unchecked), so a born-dead capture would classify 'ok'
    // and open one silent turn. Only 'ended' is checked — terminal and
    // race-free. 'muted' at birth is spec-transient (iOS tracks unmute when
    // frames flow), so treating it as dead would eat healthy first holds.
    // Death AFTER this check self-heals on the next hold: livekit's unmute
    // path re-acquires an ended mic track on its own.
    const fresh = track.mediaStreamTrack;
    if (fresh && fresh.readyState === 'ended') {
      try {
        await participant.setMicrophoneEnabled(false);
      } catch {
        /* best effort */
      }
      // Named so the classifier reports 'unavailable' (OS cannot deliver
      // capture), not a recoverable 'prompt'.
      throw Object.assign(new Error('restarted capture is already ended'), {
        name: 'NotReadableError',
      });
    }
  }
}

/**
 * Self-heal: watch for the player flipping the mic permission to Allow in browser
 * settings (Chromium fires PermissionStatus 'change') so a 'blocked' UI can clear
 * without waiting for another hold. Best-effort — unsupported elsewhere.
 * Returns a cleanup function.
 */
export function observeMicPermissionGrant(onGranted: () => void): () => void {
  let status: PermissionStatus | null = null;
  let cancelled = false;
  const onChange = () => {
    if (status?.state === 'granted') onGranted();
  };
  void (async () => {
    try {
      const s = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
      if (cancelled || !s) return;
      status = s;
      s.addEventListener?.('change', onChange);
    } catch {
      /* permissions API unsupported for microphone (Firefox/Safari) */
    }
  })();
  return () => {
    cancelled = true;
    status?.removeEventListener?.('change', onChange);
  };
}
